import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useDietContext } from "@/contexts/DietContext";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import Prisma from "@prisma/client";
import { useState } from "react";

export const DietPreferencesStep = () => {
    const { t } = useTranslation();
    const { setCurrentDietId, authFetch } = useAuth();
    const queryClient = useQueryClient();
    const { formData, setFormData, setStep } = useDietContext();
    const [progressCount, setProgressCount] = useState(0);
    let handler: NodeJS.Timeout | null = null;
    const mutation = useMutation({
        mutationFn: async () => {
            setProgressCount(0);
            handler = setInterval(() => {
                setProgressCount((prev) =>
                    prev < 80 ? prev + 1 : prev < 90 ? prev + 0.5 : prev + 0.1,
                );
            }, 1000);
            const response = await authFetch("/api/diet/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error("Failed to create diet");
            }

            const json = await response.json();
            if (!json.diet) {
                throw new Error("Missing diet data");
            }
            return json.diet;
        },
        onSuccess: (data: Prisma.Diet) => {
            clearInterval(handler);
            toast({
                title: t("buildDiet.toasts.build.title"),
                description: t("buildDiet.toasts.build.success"),
                variant: "default",
            });
            // Aggiorna la dieta attiva
            setCurrentDietId(data.id);
            queryClient.setQueryData(["diet", data.id], data);
            setStep((prev) => prev + 1);
        },
        onError: () => {
            clearInterval(handler);
            toast({
                title: t("buildDiet.toasts.build.title"),
                description: t("buildDiet.toasts.build.error"),
                variant: "destructive",
            });
        },
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleNextStep = () => {
        mutation.mutate();
    };

    const handleBackStep = () => {
        setStep((prev) => prev - 1);
    };

    return (
        <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-12">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                    {t("buildDiet.preferences.title")}
                </h1>
            </div>
            <Card className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <p className="text-sm text-gray-500">
                            {t("buildDiet.preferences.bmr")}
                        </p>
                        <p className="text-lg font-medium">
                            {formData.bmr} {t("buildDiet.preferences.kcal")}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t("buildDiet.preferences.tdee")}
                        </p>
                        <p className="text-lg font-medium">
                            {formData.tdee} {t("buildDiet.preferences.kcal")}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t("buildDiet.preferences.targetCalories")}
                        </p>
                        <p className="text-lg font-medium">
                            {formData.calories}{" "}
                            {t("buildDiet.preferences.kcal")}
                        </p>
                    </div>
                </div>
            </Card>
            <div className="space-y-4">
                <div>
                    <Label htmlFor="excludedFoods">
                        {t("buildDiet.preferences.excludedFoods")}
                    </Label>
                    <Textarea
                        id="excludedFoods"
                        name="excludedFoods"
                        value={formData.excludedFoods}
                        onChange={handleInputChange}
                        placeholder={t(
                            "buildDiet.preferences.excludedFoodsPlaceholder",
                        )}
                        className="mt-1"
                    />
                </div>

                <div>
                    <Label htmlFor="preferredFoods">
                        {t("buildDiet.preferences.preferredFoods")}
                    </Label>
                    <Textarea
                        id="preferredFoods"
                        name="preferredFoods"
                        value={formData.preferredFoods}
                        onChange={handleInputChange}
                        placeholder={t(
                            "buildDiet.preferences.preferredFoodsPlaceholder",
                        )}
                        className="mt-1"
                    />
                </div>

                <div>
                    <Label htmlFor="medicalConditions">
                        {t("buildDiet.preferences.medicalConditions")}
                    </Label>
                    <Textarea
                        id="medicalConditions"
                        name="medicalConditions"
                        value={formData.medicalConditions}
                        onChange={handleInputChange}
                        placeholder={t(
                            "buildDiet.preferences.medicalConditionsPlaceholder",
                        )}
                        className="mt-1"
                    />
                </div>
            </div>
            {/* Progresso */}
            {mutation.isPending && (
                <div className="mt-4">
                    <Progress value={progressCount} className="w-full" />
                    <p className="text-center text-sm text-gray-500 mt-2">
                        {t("buildDiet.progress")}
                    </p>
                </div>
            )}
            {/* Pulsanti Back e Next */}
            <div className="flex justify-between mt-6">
                <Button
                    variant="outline"
                    onClick={handleBackStep}
                    disabled={mutation.isPending}
                >
                    {t("buildDiet.back")}
                </Button>
                <Button onClick={handleNextStep} disabled={mutation.isPending}>
                    {mutation.isPending
                        ? t("buildDiet.submitting")
                        : t("buildDiet.next")}
                </Button>
            </div>{" "}
        </div>
    );
};
